import React, {
  Component,
  type PropsWithChildren,
  type ReactNode,
} from 'react';

import classNames from 'classnames';
import _throttle from 'lodash-es/throttle';

import styles from './resizable.module.css';

const RESIZER_CONTROL_WIDTH = 5;

type Props = PropsWithChildren<{
  'data-scroll-into-id'?: string;
  className?: string;
  defaultSize?: number;
  minWidth: number;
  maxWidth: number;
  side: 'left' | 'right';
  onResize?: (width: number) => void;
}>;

class Resizable extends Component<Props> {
  static defaultProps = {
    side: 'right',
  };

  ref = React.createRef<HTMLDivElement>();

  state = {
    resizing: false,
  };

  prevWidths: number | undefined;

  prevLeft: number | undefined;

  width = this.props.defaultSize
    ? this.props.defaultSize + RESIZER_CONTROL_WIDTH
    : this.props.minWidth + RESIZER_CONTROL_WIDTH;

  handleMouseMove = _throttle((event) => {
    const { side } = this.props;
    let width;
    const rootElement = this.ref.current;
    if (!rootElement) {
      return;
    }
    const boundingClientRect = rootElement.getBoundingClientRect();
    if (side === 'right') {
      width = Math.abs(event.clientX) - boundingClientRect.left;
    } else {
      width = boundingClientRect.right - Math.abs(event.clientX);
      this.prevLeft = boundingClientRect.left;
    }
    if (width < this.props.minWidth + RESIZER_CONTROL_WIDTH) {
      width = this.props.minWidth + RESIZER_CONTROL_WIDTH;
    }
    if (width > this.props.maxWidth) {
      width = this.props.maxWidth;
    }
    this.prevWidths = this.width;
    this.width = width + RESIZER_CONTROL_WIDTH;
    rootElement.style.width = `${this.width}px`;
    this.ensureWidth();
  }, 16);

  ensureWidth = (): void => {
    const rootElement = this.ref.current;
    if (!rootElement) {
      return;
    }
    const { side } = this.props;
    if (side !== 'left') {
      return;
    }
    const boundingClientRect = rootElement.getBoundingClientRect();
    if (boundingClientRect.left === this.prevLeft && this.prevWidths) {
      this.width = this.prevWidths;
      rootElement.style.width = `${this.width}px`;
    }
  };

  handleMouseDown = (): void => {
    this.setState({ resizing: true });
    document.addEventListener('mousemove', this.handleMouseMove);
    document.addEventListener('mouseup', this.handleMouseUp);
  };

  handleMouseUp = (): void => {
    document.removeEventListener('mousemove', this.handleMouseMove);
    this.setState({ resizing: false });
    this.props.onResize?.(this.width);
  };

  render(): ReactNode {
    const { minWidth, maxWidth, children, side } = this.props;
    return (
      <div
        data-scroll-into-id={this.props['data-scroll-into-id']}
        className={classNames(styles.root, this.props.className)}
        ref={this.ref}
        style={{
          minWidth: minWidth + RESIZER_CONTROL_WIDTH,
          maxWidth: maxWidth + RESIZER_CONTROL_WIDTH,
          width: this.width,
        }}
      >
        {side === 'right' ? children : null}
        <div
          className={classNames(styles.resizer_control, {
            [styles.active]: this.state.resizing,
          })}
          onMouseDown={this.handleMouseDown}
        />
        {side === 'left' ? children : null}
      </div>
    );
  }
}

export default Resizable;
